import { useModal } from '@ebay/nice-modal-react'
import { Link } from '@tanstack/router'
import axios from 'axios'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import bronze from '../../assets/vip/bronze.svg'
import gold from '../../assets/vip/gold.svg'
import platinum from '../../assets/vip/platinum.svg'
import silver from '../../assets/vip/silver.svg'
import { MAX_BET } from '../../constants/vip'
import { useBoundStore } from '../../store/store'
import type { ApiResponse } from '../../types/api'
import type { VipLevelObj } from '../../types/vip'
import { api } from '../../utils/api-client'
import { numberWithCommas } from '../../utils/number'
import Icon from '../icon'
import { PointsBadgeRedeem } from '../personal-data/vip-level'
import RedeemModal from './redeem-modal'

interface VipLevelProps {
  inVip?: boolean
  closeProfileDrawer?: () => void
}

const VipLevel = ({ inVip, closeProfileDrawer }: VipLevelProps) => {
  const username = useBoundStore(state => state.user.username)
  const { t } = useTranslation(['personalData', 'vip'])
  const [vipLevel, setVipLevel] = useState<VipLevelObj>({
    viplevel: '0',
    accountnum: 0,
  })
  const redeemPoints = useBoundStore(state => state.settings.redeem_points)
  const redeemModal = useModal(RedeemModal)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const fetchViplevel = async () => {
      try {
        const response = await api.post<ApiResponse<VipLevelObj>>(
          '/ajax_data.php',
          {
            type: 'get_vip_level',
          },
          { signal },
        )
        const { status, info } = response.data
        if (status === 1) {
          setVipLevel(info)
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error(error)
        }
      }
    }
    fetchViplevel()

    return () => {
      controller.abort()
    }
  }, [username])

  const getCurrentVip = useCallback(() => {
    const { viplevel: level } = vipLevel
    if (level === '0')
      return {
        src: bronze,
        label: t('bronze', { ns: 'vip' }),
      }
    if (level === '1')
      return {
        src: silver,
        label: t('silver', { ns: 'vip' }),
      }
    if (level === '2')
      return {
        src: gold,
        label: t('gold', { ns: 'vip' }),
      }
    if (level === '3')
      return {
        src: platinum,
        label: t('platinum', { ns: 'vip' }),
      }
    return {
      src: bronze,
      label: t('bronze', { ns: 'vip' }),
    }
  }, [vipLevel])

  const getNextVip = useCallback(() => {
    const { viplevel: level } = vipLevel
    if (level === '0')
      return {
        src: silver,
        label: t('silver', { ns: 'vip' }),
      }
    if (level === '1')
      return {
        src: gold,
        label: t('gold', { ns: 'vip' }),
      }
    if (level === '2')
      return {
        src: platinum,
        label: t('platinum', { ns: 'vip' }),
      }
    if (level === '3')
      return {
        src: platinum,
        label: t('platinum', { ns: 'vip' }),
      }
    return {
      src: bronze,
      label: t('bronze', { ns: 'vip' }),
    }
  }, [vipLevel])

  const getProgress = useCallback(() => {
    const { viplevel: level, accountnum } = vipLevel
    if (level === '3') return 100
    const nextVipLevel = (
      parseInt(vipLevel.viplevel) + 1
    ).toString() as VipLevelObj['viplevel']

    return (accountnum / MAX_BET[nextVipLevel]) * 100
  }, [vipLevel])

  const progress = getProgress()
  const currentVip = getCurrentVip()
  const nextVip = getNextVip()

  return (
    <div className="v-flx-ctr">
      {!inVip && (
        <div className="flx-btw-ctr" style={{ width: '100%', padding: '10px' }}>
          <p className="uppercase text-txt text-[10px] font-Syncopate-Bold">
            {currentVip.label.charAt(0) +
              currentVip.label.slice(1).toLowerCase()}
          </p>
          {redeemPoints.value ? (
            <PointsBadgeRedeem>
              <p className="label">
                {t('total-points', { ns: 'personalData' })}
              </p>
              <p className="value">{numberWithCommas(+vipLevel.accountnum)}</p>
              <button
                className="redeem"
                onClick={() => {
                  closeProfileDrawer?.()
                  redeemModal.show()
                }}>
                {t('redeem', { ns: 'referral' })}
              </button>
            </PointsBadgeRedeem>
          ) : (
            <div className="bg-tertiary text-center w-[100px] leading-3 pt-1.5 pb-[5px] px-0 rounded-[5px] xl:leading-[14px] xl:w-[120px]">
              <p className="text-[10px] text-txt mb-0.5 xl:text-xs">
                {t('total-points', { ns: 'personalData' })}
              </p>
              <p className="text-[10px] text-[#fbb03b] xl:text-xs">
                {numberWithCommas(+vipLevel.accountnum)}
              </p>
            </div>
          )}
          <div className="flx-ctr">
            <Link to="/vip" search={{ tab: 'privileges' }}>
              <button
                className="text-[#e9bf2e] uppercase text-[10px] font-Syncopate-Bold whitespace-nowrap mr-[5px] border-0 border-b-[#e9bf2e] border-b border-solid"
                onClick={closeProfileDrawer}>
                {t('vip-access', { ns: 'vip' })}
              </button>
            </Link>
            <Icon
              id="chevron-right"
              width="7"
              height="13"
              fill="#E9BF2E"
              style={{ marginTop: 1, display: 'block' }}
            />
          </div>
        </div>
      )}
      <div className="flx-ctr w-full bg-[#f2f2fa] dark:bg-[#3b3448] pt-[5px] pb-[7px] px-5 xl:pt-2 xl:px-[900px-344px] xl:pb-[10px]">
        <img
          className="w-[22px] max-h-[22px] xl:w-[30px] xl:max-h-[30px]"
          src={currentVip.src}
          alt={currentVip.label}
        />
        <div className="flx-ctr border w-[70%] mx-[7px] my-0 p-0.5 rounded-[10px] border-solid border-[#525d76] xl:flex-1 xl:p-[3px]">
          <div className="w-[5px] h-[5px] bg-[#fbb03b] rounded-[50%] xl:w-[7px] xl:h-[7px]" />
          <div className="h-[3px] flex-1 relative xl:h-[5px]">
            <p className="absolute -translate-y-2/4 -translate-x-2/4 text-txt text-[8px] font-Avenir-Black text-shadow-black-md z-[1] left-2/4 top-2/4 xl:text-[10px] xl:top-0.5">
              {progress.toFixed(2)}%
            </p>
            <div
              className="absolute left-[-3px] w-[0%] h-[3px] bg-[#fbb03b] rounded-sm top-0 xl:h-[5px]"
              style={{ width: `calc(${progress}% + 3px)` }}
            />
          </div>
          <div className="w-[5px] h-[5px] bg-[#fbb03b] rounded-[50%] xl:w-[7px] xl:h-[7px]" />
        </div>
        <img
          className="w-[22px] max-h-[22px] xl:w-[30px] xl:max-h-[30px]"
          src={nextVip.src}
          alt={nextVip.label}
        />
      </div>
      <div className="flx-ctr w-full bg-[#eaecf3] dark:bg-[#2e2a3a] h-[11px] relative px-5 py-0 xl:flex xl:justify-between xl:py-[3px] xl:px-[(900px-344px)/2]">
        <p className="text-white text-txt text-[8px] font-Syncopate-Bold absolute text-center left-[30px] uppercase top-0.5 xl:text-xs xl:static xl:-top-px">
          {currentVip.label}
        </p>
        <p className="text-white text-txt text-[8px] font-Syncopate-Bold absolute text-center right-[30px] uppercase top-0.5 xl:text-xs xl:static xl:-top-px">
          {nextVip.label}
        </p>
      </div>
    </div>
  )
}

export default VipLevel
